import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Link,
  MenuItem,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import BuildCircleOutlinedIcon from '@mui/icons-material/BuildCircleOutlined';
import SendIcon from '@mui/icons-material/Send';
import api from '../api';
import { Link as RouterLink } from 'react-router-dom';

const issueTypes = [
  'Sign-in or account access',
  'Page or feature not loading',
  'Payment or subscription',
  'Website or app error',
  'Other technical issue',
];

const Helpdesk: React.FC = () => {
  const [form, setForm] = useState({
    name: '',
    email: '',
    issueType: issueTypes[0],
    subject: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);

  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLoading(true);
    try {
      const response = await api.post('/contact', {
        name: form.name,
        email: form.email,
        subject: `[${form.issueType}] ${form.subject}`,
        message: form.message,
        type: 'technical-support',
      });
      if (!response.data?.success) {
        throw new Error(response.data?.message || 'Your support request could not be submitted. Please try again.');
      }
      setFeedback({
        message: 'Your technical support request has been submitted. Our team can follow up using the email address you provided.',
        severity: 'success',
      });
      setForm({ name: '', email: '', issueType: issueTypes[0], subject: '', message: '' });
    } catch (error: any) {
      setFeedback({
        message: error?.response?.data?.message || error?.message || 'Your support request could not be submitted. Please try again.',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', py: { xs: 4, md: 8 }, background: (theme) => theme.palette.mode === 'dark'
      ? 'radial-gradient(ellipse at top, rgba(25, 118, 210, 0.18), transparent 55%)'
      : 'radial-gradient(ellipse at top, rgba(25, 118, 210, 0.10), transparent 55%)' }}>
      <Container maxWidth="md">
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 68,
              height: 68,
              mb: 2,
              borderRadius: 3,
              color: 'primary.main',
              bgcolor: 'action.hover',
            }}
          >
            <BuildCircleOutlinedIcon sx={{ fontSize: 40 }} />
          </Box>
          <Typography variant="h3" component="h1" fontWeight={800} gutterBottom sx={{ fontSize: { xs: '2rem', sm: '2.75rem' } }}>
            Technical Helpdesk
          </Typography>
          <Typography color="text.secondary" sx={{ maxWidth: 650, mx: 'auto', lineHeight: 1.7 }}>
            Tell our technical support team what is going wrong with the website or app. Include the steps that led to the issue so we can investigate and follow up by email.
          </Typography>
        </Box>

        <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
          Use this Helpdesk for login failures, broken pages, payment problems, or other technical issues. Please do not include your password or other sensitive information.
        </Alert>

        <Card sx={{ borderRadius: 3, boxShadow: (theme) => theme.shadows[5] }}>
          <CardContent sx={{ p: { xs: 2.5, sm: 4 } }}>
            <Typography variant="h5" component="h2" fontWeight={700} gutterBottom>
              Raise a technical support request
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Leave your contact email and a clear description. This form creates a support request; it is not a live chat.
            </Typography>
            <Box component="form" onSubmit={submit} sx={{ display: 'grid', gap: 2.5 }}>
              <TextField
                label="Your name"
                autoComplete="name"
                required
                value={form.name}
                onChange={(event) => setForm({ ...form, name: event.target.value })}
              />
              <TextField
                label="Email address"
                type="email"
                autoComplete="email"
                required
                value={form.email}
                onChange={(event) => setForm({ ...form, email: event.target.value })}
              />
              <TextField
                select
                label="What do you need help with?"
                value={form.issueType}
                onChange={(event) => setForm({ ...form, issueType: event.target.value })}
              >
                {issueTypes.map((issueType) => (
                  <MenuItem key={issueType} value={issueType}>{issueType}</MenuItem>
                ))}
              </TextField>
              <TextField
                label="Short summary"
                required
                value={form.subject}
                onChange={(event) => setForm({ ...form, subject: event.target.value })}
              />
              <TextField
                label="Describe the issue"
                required
                multiline
                minRows={5}
                helperText="What did you expect to happen, and what happened instead?"
                value={form.message}
                onChange={(event) => setForm({ ...form, message: event.target.value })}
              />
              <Button type="submit" variant="contained" size="large" startIcon={<SendIcon />} disabled={loading}>
                {loading ? 'Submitting...' : 'Submit support request'}
              </Button>
            </Box>
          </CardContent>
        </Card>

        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Need help with lessons, exams, or school work?{' '}
            <Link component={RouterLink} to="/high-school/contact" underline="hover" fontWeight={700}>
              Contact Student Support
            </Link>
            .
          </Typography>
        </Box>

        <Snackbar open={Boolean(feedback)} autoHideDuration={7000} onClose={() => setFeedback(null)}>
          {feedback ? <Alert severity={feedback.severity} onClose={() => setFeedback(null)}>{feedback.message}</Alert> : undefined}
        </Snackbar>
      </Container>
    </Box>
  );
};

export default Helpdesk;
